/*
 * <product-addons>
 *
 * Drives the CHAG "Add a little extra?" add-on checkboxes and the running
 * price-total table on the product page.
 *
 * Behaviour:
 *  - Keeps a live Product price / Total options / Order total table in sync as
 *    the shopper toggles add-on checkboxes, changes the variant, or changes the
 *    quantity stepper.
 *  - Counter add-ons (everything except the Logo Gift Box): ticking the box
 *    swaps the checkmark for a "- N +" stepper so multiples of the same add-on
 *    can be chosen. Quantities are per-hamper: cart quantity = stepper count x
 *    parent quantity. "-" at 1 reverts to the unticked checkbox.
 *  - Hijacks the parent product form submit. When one or more add-ons are
 *    checked it posts a single /cart/add.js request with an `items` array
 *    (parent variant first, then each checked add-on as its own line item) so
 *    everything lands in the cart in one AJAX flow and the cart drawer opens
 *    once. When nothing is checked it leaves Dawn's product-form.js to handle
 *    the normal single-item add.
 *
 * Add-on products are real companion products (handles addon-*, tagged addon),
 * referenced from the parent via metafield custom.addon_products.
 */
if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      constructor() {
        super();
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
        // Compare-at (full) price of the current variant, 0 when not on sale.
        this.comparePrice = parseInt(this.dataset.comparePrice, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.checkboxes = Array.from(this.querySelectorAll('[data-addon-checkbox]'));
      }

      connectedCallback() {
        this.productForm = document.getElementById(this.dataset.productFormId);
        this.productFormElement = this.productForm ? this.productForm.closest('product-form') : null;
        // The quantity <input> lives in the quantity_selector block, OUTSIDE the
        // <form> element, and is associated via its form="" attribute, so
        // querySelector on the form never finds it. form.elements does; fall
        // back to the section's #Quantity- id. (Fixed 2026-09-07: quantity had
        // been silently treated as 1 for the live total AND the cart submit.)
        this.quantityInput =
          (this.productForm && this.productForm.elements.namedItem('quantity')) ||
          document.querySelector(`#Quantity-${this.dataset.sectionId}`);

        this.checkboxes.forEach((cb) => {
          cb.addEventListener('change', () => {
            this.syncStepper(cb);
            this.updateTotals();
          });
          this.bindStepper(cb);
        });

        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', () => this.updateTotals());
        }

        // Keep base price in sync when the variant changes (Dawn re-renders the
        // section, so re-bind to the fresh quantity input as well).
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (!variant || event.data.sectionId !== this.dataset.sectionId) return;
            this.basePrice = variant.price;
            this.comparePrice = variant.compare_at_price || 0;
            this.updateTotals();
          });
        }

        // Intercept the add-to-cart submit so checked add-ons are added as
        // separate line items in one flow.
        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onSubmit.bind(this), true);
        }

        this.updateTotals();
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
      }

      get quantity() {
        const qty = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
        return Number.isNaN(qty) || qty < 1 ? 1 : qty;
      }

      get checkedAddons() {
        return this.checkboxes.filter((cb) => cb.checked && !cb.disabled);
      }

      // Per-hamper quantity for an add-on. Counter add-ons carry it in
      // dataset.addonQty (set by the stepper); plain checkboxes (Logo Gift
      // Box) are always 1 when ticked.
      addonQty(cb) {
        if (!cb.checked || cb.disabled) return 0;
        const qty = parseInt(cb.dataset.addonQty, 10);
        return Number.isNaN(qty) || qty < 1 ? 1 : qty;
      }

      /*
       * Stepper (counter add-ons only): ticking the checkbox swaps the
       * checkmark for a "- N +" stepper. "+" increments; "-" decrements and,
       * at 1, unticks the box which restores the checkmark. The buttons live
       * INSIDE the <label>, so their click handlers must preventDefault to
       * stop the label from also toggling the checkbox.
       */
      bindStepper(cb) {
        if (!cb.hasAttribute('data-addon-counter')) return;
        const label = cb.closest('.product-addons__label');
        const stepper = label ? label.querySelector('[data-addon-stepper]') : null;
        if (!stepper) return;

        stepper.querySelector('[data-addon-qty-plus]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setAddonQty(cb, this.addonQty(cb) + 1);
        });
        stepper.querySelector('[data-addon-qty-minus]')?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const qty = this.addonQty(cb);
          if (qty > 1) {
            this.setAddonQty(cb, qty - 1);
          } else {
            // Back to the unticked checkbox state.
            cb.checked = false;
            cb.dispatchEvent(new Event('change'));
          }
        });
      }

      setAddonQty(cb, qty) {
        cb.dataset.addonQty = qty;
        const label = cb.closest('.product-addons__label');
        const display = label ? label.querySelector('[data-addon-qty-display]') : null;
        if (display) display.textContent = qty;
        this.updateTotals();
      }

      // Show/hide the stepper to match the checkbox state (counter add-ons).
      syncStepper(cb) {
        if (!cb.hasAttribute('data-addon-counter')) return;
        const label = cb.closest('.product-addons__label');
        const stepper = label ? label.querySelector('[data-addon-stepper]') : null;
        if (!label || !stepper) return;

        if (cb.checked) {
          this.setAddonQty(cb, 1);
          label.classList.add('product-addons__label--counting');
          stepper.classList.remove('hidden');
          stepper.removeAttribute('aria-hidden');
          stepper.querySelectorAll('button').forEach((btn) => btn.removeAttribute('tabindex'));
        } else {
          delete cb.dataset.addonQty;
          label.classList.remove('product-addons__label--counting');
          stepper.classList.add('hidden');
          stepper.setAttribute('aria-hidden', 'true');
          stepper.querySelectorAll('button').forEach((btn) => btn.setAttribute('tabindex', '-1'));
        }
      }

      updateTotals() {
        // CHAG v2: only the single "Order total" is shown on the PDP. It equals
        // (base variant price + selected add-on prices) x quantity. The former
        // "Product price" / "Total options" cells were removed from the markup.
        const qty = this.quantity;
        // Per-hamper options total: each add-on price x its stepper count
        // (plain checkboxes count as 1). Multiplied out by the hamper qty.
        const optionsUnit = this.checkedAddons.reduce(
          (sum, cb) => sum + (parseInt(cb.dataset.addonPrice, 10) || 0) * this.addonQty(cb),
          0
        );
        const orderTotal = (this.basePrice + optionsUnit) * qty;

        this.setCell('order', orderTotal);
        // Struck-through full price: same maths on the compare-at price, so the
        // gap between the two figures is exactly the variant's discount.
        this.setCompare(this.comparePrice > this.basePrice ? (this.comparePrice + optionsUnit) * qty : 0);
      }

      setCompare(cents) {
        const scope = this.closest('product-info') || document;
        const el = scope.querySelector('[data-addon-compare="order"]');
        if (!el) return;
        if (cents > 0) {
          const label = el.querySelector('.visually-hidden');
          el.textContent = this.formatMoney(cents);
          if (label) el.prepend(label);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      }

      setCell(name, cents) {
        // 2026-09-07: the total now lives beside the quantity stepper (outside
        // <product-addons>), so search the whole product-info block first.
        const scope = this.closest('product-info') || document;
        const cell = scope.querySelector(`[data-addon-total="${name}"]`) || this.querySelector(`[data-addon-total="${name}"]`);
        if (cell) cell.textContent = this.formatMoney(cents);
      }

      onSubmit(event) {
        const addons = this.checkedAddons;
        if (addons.length === 0) return; // let product-form.js handle the simple case

        event.preventDefault();
        event.stopImmediatePropagation();

        const qty = this.quantity;
        const items = [];

        const parentId = this.productForm.querySelector('input[name="id"]')?.value;
        if (parentId) items.push({ id: parentId, quantity: qty });

        addons.forEach((cb) => {
          // `_parent` (underscore = hidden at checkout) links the add-on line to
          // its parent variant so the cart drawer/page can group it below the
          // product it was added onto. Quantity is per-hamper: stepper count
          // (1 for plain checkboxes) x hamper quantity.
          const item = { id: cb.value, quantity: this.addonQty(cb) * qty };
          if (parentId) item.properties = { _parent: parentId };
          items.push(item);
        });

        this.addItems(items);
      }

      addItems(items) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const submitButton = this.productForm.querySelector('[type="submit"]');
        const spinner = submitButton ? submitButton.querySelector('.loading__spinner') : null;

        if (submitButton) {
          submitButton.setAttribute('aria-disabled', true);
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleError(response);
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-addons',
                cartData: response,
              });
            }
            cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      handleError(response) {
        // Surface the error through the product form's existing message slot.
        if (this.productFormElement && typeof this.productFormElement.handleErrorMessage === 'function') {
          this.productFormElement.handleErrorMessage(response.description || response.message);
        } else {
          console.error(response.description || response.message);
        }
      }

      formatMoney(cents) {
        const value = (cents || 0) / 100;
        const format = this.moneyFormat;
        // Supports the common {{amount}} / {{amount_with_comma_separator}} /
        // {{amount_no_decimals}} placeholders used in shop.money_format.
        const formatWithDecimals = (decimalPlaces, thousands, decimal) => {
          const fixed = value.toFixed(decimalPlaces);
          const parts = fixed.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts.join(decimal);
        };

        return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
          switch (placeholder) {
            case 'amount':
              return formatWithDecimals(2, ',', '.');
            case 'amount_no_decimals':
              return formatWithDecimals(0, ',', '.');
            case 'amount_with_comma_separator':
              return formatWithDecimals(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return formatWithDecimals(0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return formatWithDecimals(2, "'", '.');
            default:
              return formatWithDecimals(2, ',', '.');
          }
        });
      }
    }
  );
}
